// src/pages/Accounts.jsx
// Control Panel → Accounts (3.10, Super Admins only). Every client account:
// business (faxes by email to fax@crazypixels.org, its people by email
// domain) or individual (privatefax@crazypixels.org), its fax numbers, who
// gets its incoming faxes, and where its files are kept. Create
// a new account or change one. People are added in Users → People with the
// account picked in the Account box; rates in Control Panel → Billing.
import { phone as checkPhone, display as showPhone, PHONE_HELP } from '../utils/phone';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { apiCall } from '../components/EmailEditorParts';
import MultiSearchSelect from '../components/MultiSearchSelect';
import ShowSelect from '../components/ShowSelect';
import SortTh, { useSort } from '../components/SortTh';
import { pathForTab } from '../utils/tabRoutes';
import ClientDocuments from '../components/ClientDocuments';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { THEMES, themeLabel } from '../theme/themes';
import { APPEARANCES } from '../theme/useAppearance';
import OfficeHoursFields, { DEFAULT_HOURS, hoursText, hoursProblem } from '../components/OfficeHoursFields';

const call = (path, getToken, options) => apiCall(`/portal/accounts${path}`, getToken, options);
const fmtFax = num => {
  const n = String(num || '').replace(/\D/g, '');
  return n.length === 11 ? `+1 (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}` : num;
};
const TELNYX_APP = { business: 'Crazypixels Fax Gateway', individual: 'Private-FAX' };
const EMPTY = { name: '', type: 'business', numbers: [{ number: '', label: 'Main line' }], domains: '', incoming: '', incomingCc: '', incomingBcc: '', hipaa: false, newUntilOpened: null, defaultTeamName: 'Main', autoCover: true, officePhone: '', whatsapp: '', complianceReport: null, emailTeamMembers: true, officeHours: DEFAULT_HOURS, sendConfirm: false };   // newUntilOpened null: the type's default; 3.61.0 office hours
const toList = v => String(v || '').split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
const toText = v => [].concat(v || []).join(', ');

// "2 hours ago" / "3 days ago"
const ago = iso => {
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60); if (h < 48) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24); return `${d} day${d === 1 ? '' : 's'} ago`;
};
// "(555) 010-0199: 12 earlier faxes (May 3 – Oct 4, 9 with files), 2 failed incoming calls."
const shortDate = iso => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const earlierText = p => {
  const parts = [];
  if (p.faxes) parts.push(`${p.faxes} earlier fax${p.faxes === 1 ? '' : 'es'} (${p.withFiles} with ${p.withFiles === 1 ? 'its file' : 'files'} still kept)`);
  if (p.failures) parts.push(`${p.failures} failed incoming call${p.failures === 1 ? '' : 's'}`);
  const when = p.first ? ` from ${shortDate(p.first)}${p.last && shortDate(p.last) !== shortDate(p.first) ? ` to ${shortDate(p.last)}` : ''}` : '';
  return `${fmtFax(p.number)}: ${parts.join(' and ')}${when}.`;
};

// When the Super Admin chose to move a new number's earlier faxes (3.21.0):
// say how many moved
const claimedText = c => [
  c?.faxes ? `${c.faxes} earlier fax${c.faxes === 1 ? '' : 'es'} moved to this client (${c.filesMoved} file${c.filesMoved === 1 ? '' : 's'} to its folder); they aren't billed.` : '',
  c?.failures ? `${c.failures} failed incoming call${c.failures === 1 ? '' : 's'} too.` : '',
  c?.error || '',
].filter(Boolean).join(' ') || undefined;

export default function Accounts() {
  const { getToken, effectiveInfo, refreshInfo } = useAuth();
  const [list, setList] = useState(null);
  // 3.21.1: faxes on numbers no client has (a missed setup)
  const [unassigned, setUnassigned] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | account
  const [created, setCreated] = useState(null);
  // Compact list (3.18.8): one row per client, details on click; search by
  // client name (3.18.9: name only), sortable columns, Show 50 by default
  const [openId, setOpenId] = useState(null);
  const [terms, setTerms] = useState([]);
  const [showN, setShowN] = useState('50');

  const load = useCallback(async () => {
    setError('');
    try { setList((await call('', getToken)).data); } catch (e) { setError(e.message); }
    try { setUnassigned((await call('/unassigned', getToken)).data || []); } catch { /* the warning is optional */ }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  // Keep the dot on the Control Panel tab in step with the warning
  const dot = effectiveInfo?.unassignedNumbers || 0;
  useEffect(() => { if (list && unassigned.length !== dot) refreshInfo?.(true); }, [list, unassigned.length, dot, refreshInfo]);

  const all = list || [];
  const options = [...new Set(all.map(a => a.name))].sort((x, y) => x.localeCompare(y));
  const ts = terms.map(t => String(t).toLowerCase());
  const matching = !ts.length ? all : all.filter(a => ts.some(t => a.name.toLowerCase().includes(t)));
  const { sorted, sort, onSort } = useSort(matching, {
    name: a => a.name, numbers: a => (a.numbers[0]?.number || '').replace(/\D/g, ''), email: a => a.emailAddress, people: a => a.people || 0,
  }, { key: 'name', dir: 1 });
  const shown = showN === 'all' ? sorted : sorted.slice(0, Number(showN) || 50);

  if (error) return <div role="alert" style={S.error}>{error}</div>;
  if (!list) return <Loader label="Loading clients…" />;


  return (
    <div>
      <div style={S.top}>
        <div>
          <h3 style={S.title}>Clients</h3>
          <p style={S.sub}>Every client, its fax numbers and who gets its faxes. Each client's faxes, files, usage and emails are kept apart from every other client's.</p>
        </div>
        {!editing && <button onClick={() => { setEditing('new'); setCreated(null); }} style={S.primaryBtn}>＋ New client</button>}
      </div>

      {unassigned.length > 0 && (
        <div role="alert" style={S.warn}>
          <strong><Emo e="⚠️" /> Faxes on numbers no client has</strong>
          <ul style={S.warnList}>
            {unassigned.map(u => (
              <li key={u.number}>
                <strong>{fmtFax(u.number)}</strong>: {[
                  u.faxes ? `${u.faxes} fax${u.faxes === 1 ? '' : 'es'}` : '',
                  u.failures ? `${u.failures} failed incoming call${u.failures === 1 ? '' : 's'}` : '',
                ].filter(Boolean).join(' and ')}, {shortDate(u.first)}{shortDate(u.last) !== shortDate(u.first) ? ` – ${shortDate(u.last)}` : ''} (latest {ago(u.last)})
                {u.faxes > 0 && <> · <a href={`/portal/inbox?to=${encodeURIComponent(u.number)}`} style={S.warnLink}>View faxes</a></>}
              </li>
            ))}
          </ul>
          <span style={S.warnHint}>
            Add the number to a client to see them there, or leave it if it's not in use. Until then only Super Admins see these faxes{effectiveInfo?.accountId !== 'all' ? ', on All clients' : ''}, and they're emailed to admin@ only.
          </span>
        </div>
      )}

      {created && (
        <div style={S.next} role="status">
          <strong>{created.name} is set up.</strong> Next:
          <ol style={S.steps}>
            <li>In Telnyx, assign {created.numbers.map(n => fmtFax(n.number)).join(', ')} to the <strong>{TELNYX_APP[created.type]}</strong> application (no messaging profile needed).</li>
            <li>Add its people in <strong>Users → People</strong> with <strong>{created.name}</strong> picked in the Client box.{created.team && <> They join its default team, <strong>{created.team}</strong>.</>}</li>
            {created.team && <li>Its teams are in <a href={`${pathForTab('users')}/teams?client=${encodeURIComponent(created.id)}`} style={S.link}>Users → Teams</a>.</li>}
            <li>Check its rates in <strong>Console → Billing</strong>.</li>
          </ol>
        </div>
      )}

      {editing === 'new' && (
        <AccountForm account={null} getToken={getToken}
          onCancel={() => setEditing(null)}
          onSaved={async (acc, isNew) => { setEditing(null); if (isNew) setCreated(acc); await load(); }} />
      )}

      <div style={S.toolbar}>
        <div style={{ minWidth: 260 }}>
          <MultiSearchSelect values={terms} onChange={setTerms} options={options} placeholder="Search client name" />
        </div>
        <ShowSelect value={showN} onChange={setShowN} groupStyle={S.showGroup} labelStyle={S.showLabel} style={S.select} />
        <span style={S.muted}>{shown.length < matching.length ? `${shown.length} of ${matching.length}` : `${matching.length} client${matching.length === 1 ? '' : 's'}`}</span>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="name" label="Client" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="numbers" label="Fax numbers" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="email" label="Faxes by email to" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="people" label="People" sort={sort} onSort={onSort} style={{ ...S.th, textAlign: 'right' }} />
            <th style={S.th}><span style={S.srOnly}>Details</span></th>
          </tr></thead>
          <tbody>
            {!shown.length ? <tr><td colSpan={5} style={S.empty}>No client matches your search.</td></tr>
              : shown.map((a, i) => {
                const open = openId === a.id;
                return [
                  <tr key={a.id} onClick={() => setOpenId(open ? null : a.id)} aria-expanded={open}
                    style={{ ...S.listRow, background: open ? 'var(--c-e0f2f1)' : i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                    <td style={S.td}><strong>{a.name}</strong> <span style={a.type === 'individual' ? S.badgeInd : S.badgeBiz}>{a.type === 'individual' ? 'Individual' : 'Business'}</span></td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{a.numbers.map(n => fmtFax(n.number)).join(', ') || '—'}</td>
                    <td style={S.td}>{a.emailAddress}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{a.people}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: 'var(--t-00695c)', whiteSpace: 'nowrap' }}>{open ? '▲ Hide' : '▼ Details'}</td>
                  </tr>,
                  open && (
                    <tr key={`${a.id}:details`}>
                      <td colSpan={5} style={S.detailCell}>
                        {editing && editing !== 'new' && editing.id === a.id ? (
                          <AccountForm account={a} getToken={getToken} onCancel={() => setEditing(null)}
                            onSaved={async () => { setEditing(null); await load(); }} />
                        ) : (
                          <>
                            <dl style={S.facts}>
                              <Fact label="Fax numbers" value={a.numbers.map(n => `${fmtFax(n.number)}${n.label ? ` (${n.label})` : ''}`).join(' · ') || '—'} />
                              <Fact label="Faxes by email to" value={a.emailAddress} />
                              {a.type === 'business' && <Fact label="People from" value={a.domains.join(', ') || 'Only people added by name'} />}
                              <Fact label="Received faxes emailed to" value={toText(a.notify.incoming) || (a.notify.incomingCc?.length || a.notify.incomingBcc?.length ? '— (Cc / Bcc only)' : 'Not set')} />
                              {a.notify.incomingCc?.length > 0 && <Fact label="Cc" value={toText(a.notify.incomingCc)} />}
                              {a.notify.incomingBcc?.length > 0 && <Fact label="Bcc" value={toText(a.notify.incomingBcc)} />}
                              {a.type === 'business' && <Fact label="Team faxes" value={a.emailTeamMembers !== false ? 'Each team member, when the team has no email of its own' : 'The team’s email, or the list above'} />}
                              <Fact label="People" value={a.people} />
                              <Fact label="Handles PHI (HIPAA)" value={a.hipaa ? <>Yes: <Emo e="🛡️" /> badge, secured fax emails, one number per fax email, confidentiality notice on</> : 'No: full fax emails with the PDF, up to 10 numbers per fax email'} />
                              <Fact label="Compliance report" value={a.complianceReport ? 'On: people whose role allows it can download it' : 'Off: only Fax Portal support can download it'} />
                              <Fact label="Send Confirmation" value={a.sendConfirm ? 'On: Compose asks to check the receivers and documents before each fax' : 'Off'} />
                              <Fact label="Cover sheet on emailed faxes" value={a.autoCover !== false ? 'Automatic' : 'Off'} />
                              <Fact label="Office contact number" value={a.officePhone ? showPhone(a.officePhone) : 'Not set'} />
                              <Fact label="WhatsApp number" value={a.whatsapp ? showPhone(a.whatsapp) : 'Not set'} />
                              <Fact label="Office hours" value={`${hoursText(a.officeHours)}${a.officeHoursSet ? '' : ' (standard)'}`} />
                              {a.type === 'business' && <Fact label="Teams" value={<>{(a.teams || []).map(t => `${t.name} (${fmtFax(t.number)})${t.name === a.defaultTeam ? ' · default' : ''}`).join(' · ') || 'None yet'}
                                {' '}<a href={`${pathForTab('users')}/teams?client=${encodeURIComponent(a.id)}`} onClick={e => e.stopPropagation()} style={S.link}>Users → Teams</a></>} />}
                              <Fact label="Files kept in" value={`${a.folder}/…`} />
                              {/* 3.46.0: the colours its people see until they pick their own */}
                              <Fact label="Starting theme" value={<ClientLook what="theme" account={a} getToken={getToken} onSaved={load} />} />
                              {/* 3.47.0: light · auto · dark, and one click to give everyone the client's look */}
                              <Fact label="Starting mode" value={<ClientLook what="mode" account={a} getToken={getToken} onSaved={load} />} />
                              <Fact label="Everyone at this client" value={<ApplyLook account={a} getToken={getToken} />} />
                            </dl>
                            {!editing && <button onClick={e => { e.stopPropagation(); setEditing(a); setCreated(null); }} style={{ ...S.primaryBtn, marginTop: 12 }}>Edit {a.name}</button>}
                            {/* Signed contracts and other papers (3.36.0) */}
                            <ClientDocuments account={a} />
                          </>
                        )}
                      </td>
                    </tr>
                  ),
                ];
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Teal · Ocean · Plum (3.46.0) and Light · Auto · Dark (3.47.0) for a client:
// its people see these until they pick their own (Preferences, their menu or
// the header; their pick wins). Each saves at once. "Apply to everyone now"
// clears everyone's own picks at this client, so all of them see the
// client's choice from their next page load (they can still change it).
const MODE_CHOICES = APPEARANCES.map(a => ({ id: a.id, label: a.label, icon: a.icon, hint: a.hint }));
const chip = (on, ring) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 5px', borderRadius: 99, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
  fontFamily: 'inherit', background: on ? 'var(--c-e0f2f1)' : 'var(--c-ffffff)', color: 'var(--t-263238)', border: on ? `2px solid ${ring}` : '1.5px solid var(--c-cfd8dc)' });

function ClientLook({ account, getToken, onSaved, what }) {
  const { notify } = useNotify();
  const [busy, setBusy] = useState('');
  const current = what === 'theme' ? (account.theme || 'teal') : (account.mode || 'auto');
  async function pick(id) {
    if (id === current || busy) return;
    setBusy(id);
    try {
      await call(`/${encodeURIComponent(account.id)}/theme`, getToken, { method: 'PUT', body: JSON.stringify({ [what]: id }) });
      const label = what === 'theme' ? themeLabel(id) : MODE_CHOICES.find(m => m.id === id)?.label;
      notify({ tone: 'success', title: `${account.name} starts on ${label}`, detail: `Its people see it from their next visit, unless they picked their own ${what === 'theme' ? 'theme' : 'light or dark'}.` });
      await onSaved();
    } catch (x) { notify({ tone: 'error', title: "Couldn't save it", detail: x.message }); }
    setBusy('');
  }
  const list = what === 'theme' ? THEMES : MODE_CHOICES;
  return (
    <span role="radiogroup" aria-label={`Starting ${what === 'theme' ? 'theme' : 'light or dark'} for ${account.name}`} style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
      {list.map(t => {
        const on = current === t.id;
        const ring = what === 'theme' ? t.preview.light.btn[1] : 'var(--c-00796b)';
        return (
          <button key={t.id} type="button" role="radio" aria-checked={on} disabled={!!busy} onClick={() => pick(t.id)} title={what === 'theme' ? t.blurb : t.hint} style={chip(on, ring)}>
            {what === 'theme'
              ? <span aria-hidden="true" style={{ width: 16, height: 16, borderRadius: '50%', background: `linear-gradient(135deg, ${t.preview.light.btn[0]} 50%, ${t.preview.light.btn[2]} 50%)` }} />
              : <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1, paddingLeft: 3 }}><Emo e={t.icon} /></span>}
            {busy === t.id ? 'Saving…' : t.label}
          </button>
        );
      })}
    </span>
  );
}

function ApplyLook({ account, getToken }) {
  const { notify, confirm } = useNotify();
  const [busy, setBusy] = useState(false);
  async function apply(e) {
    e.stopPropagation();
    const look = `${themeLabel(account.theme)}, ${MODE_CHOICES.find(m => m.id === (account.mode || 'auto'))?.label}`;
    const ok = await confirm({
      title: `Use ${look} for everyone at ${account.name}?`,
      message: `Everyone at ${account.name} switches to ${look} from their next page load, including people who picked their own. They can still change it for themselves afterwards. Someone who belongs to more than one client gets it everywhere.`,
      confirmLabel: 'Apply to everyone',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await call(`/${encodeURIComponent(account.id)}/theme/apply`, getToken, { method: 'POST' });
      notify({ tone: 'success', title: `${look} for everyone at ${account.name}`, detail: `${r.people} ${r.people === 1 ? 'person' : 'people'} see it from their next page load.` });
    } catch (x) { notify({ tone: 'error', title: "Couldn't apply it", detail: x.message }); }
    setBusy(false);
  }
  return (
    <button type="button" onClick={apply} disabled={busy} style={{ ...S.ghostBtn, padding: '5px 12px', fontSize: 12.5 }}
      title="Clear everyone's own theme and light or dark picks at this client, so they all see the client's choice">
      {busy ? 'Applying…' : <><Emo e="👥" /> Apply to everyone now</>}
    </button>
  );
}

function Fact({ label, value }) {
  return <div style={{ minWidth: 0 }}><dt style={S.dt}>{label}</dt><dd style={S.dd}>{value}</dd></div>;
}

function AccountForm({ account, getToken, onCancel, onSaved }) {
  const { notify, confirm } = useNotify();
  const isNew = !account;
  const [f, setF] = useState(() => (isNew ? EMPTY : {
    name: account.name, type: account.type, numbers: account.numbers.length ? account.numbers.map(n => ({ number: n.number, label: n.label || '' })) : [{ number: '', label: 'Main line' }],
    domains: toText(account.domains), incoming: toText(account.notify.incoming), incomingCc: toText(account.notify.incomingCc), incomingBcc: toText(account.notify.incomingBcc), hipaa: !!account.hipaa, newUntilOpened: account.newUntilOpened,
    autoCover: account.autoCover !== false, officePhone: showPhone(account.officePhone),   // 3.49.9; 3.49.17 shown as (800) 766-8732
    whatsapp: showPhone(account.whatsapp),   // 3.57.3
    complianceReport: account.complianceReport,   // 3.49.21
    sendConfirm: !!account.sendConfirm,   // 3.64.0
    emailTeamMembers: account.emailTeamMembers !== false,   // 3.59.2
    officeHours: account.officeHours || DEFAULT_HOURS,   // 3.61.0
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  // Compliance report (3.49.21): unless picked, on for business, off for individual
  const cr = typeof f.complianceReport === 'boolean' ? f.complianceReport : f.type === 'business';
  const setNum = (i, k, v) => setF(x => ({ ...x, numbers: x.numbers.map((n, j) => (j === i ? { ...n, [k]: v } : n)) }));

  async function save(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    const body = {
      name: f.name, type: f.type, numbers: f.numbers.filter(n => n.number.trim()), domains: toList(f.domains),
      notify: { incoming: toList(f.incoming), incomingCc: toList(f.incomingCc), incomingBcc: toList(f.incomingBcc) },
      hipaa: !!f.hipaa, autoCover: !!f.autoCover, officePhone: checkPhone(f.officePhone).e164 ?? f.officePhone.trim(),
      whatsapp: checkPhone(f.whatsapp).e164 ?? f.whatsapp.trim(),
      complianceReport: cr,
      sendConfirm: !!f.sendConfirm,   // 3.64.0
      ...(f.type === 'business' ? { emailTeamMembers: !!f.emailTeamMembers } : {}),   // 3.59.2
      officeHours: f.officeHours,   // 3.61.0
      ...(isNew && f.type === 'business' ? { defaultTeamName: f.defaultTeamName } : {}),   // 3.19.10
    };
    try {
      // 3.21.0: a number being added that already received faxes while no
      // client had it — ask whether they move to this client (never billed)
      const earlier = (await call('/earlier-faxes', getToken, { method: 'POST', body: JSON.stringify({ id: account?.id || null, numbers: body.numbers.map(n => n.number) }) })).data || [];
      if (earlier.length) {
        const pick = await confirm({
          title: earlier.length === 1 ? `${fmtFax(earlier[0].number)} already has faxes` : 'These numbers already have faxes',
          message: `${earlier.map(earlierText).join(' ')} They arrived while no client had ${earlier.length === 1 ? 'this number' : 'these numbers'}, so today only Super Admins see them.`,
          choices: [
            { value: 'keep', label: 'Save the number only', hint: ' — the earlier faxes stay with Super Admins only.' },
            { value: 'move', label: `Save and move them to ${f.name || 'this client'}`, hint: ` — its people can see them and the files move to its folder. They are never counted in ${f.name || 'this client'}'s usage or billing.` },
          ],
          confirmLabel: 'Save',
        });
        if (!pick) { setBusy(false); return; }
        body.moveEarlierFaxes = pick === 'move';
      }
      if (isNew) {
        const r = await call('', getToken, { method: 'POST', body: JSON.stringify(body) });
        notify({ tone: 'success', title: `${f.name} created`, detail: claimedText(r.claimed) });
        onSaved({ id: r.id, name: f.name, type: f.type, numbers: body.numbers, team: f.type === 'business' ? (f.defaultTeamName || 'Main') : null }, true);
      } else {
        const r = await call(`/${account.id}`, getToken, { method: 'PUT', body: JSON.stringify(body) });
        notify({ tone: 'success', title: `${f.name} saved`, detail: claimedText(r.claimed) });
        onSaved(null, false);
      }
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  }

  return (
    <form onSubmit={save} style={S.form} aria-label={isNew ? 'New client' : `Edit ${account.name}`}>
      <h4 style={S.formTitle}>{isNew ? 'New client' : `Edit ${account.name}`}</h4>
      {isNew ? (
        <fieldset style={S.fieldset}>
          <legend style={S.label}>Type</legend>
          <label style={S.choice}><input type="radio" checked={f.type === 'business'} onChange={() => set('type', 'business')} /> <span><strong>Business</strong>: several people, who fax by email to fax@crazypixels.org</span></label>
          <label style={S.choice}><input type="radio" checked={f.type === 'individual'} onChange={() => set('type', 'individual')} /> <span><strong>Individual</strong>: one person, who faxes by email to privatefax@crazypixels.org</span></label>
        </fieldset>
      ) : <p style={S.hint}>{account.type === 'individual' ? 'Individual' : 'Business'} client. The type can't change once it's set up.</p>}
      {/* Business clients start with a default team (3.19.10) */}
      {isNew && f.type === 'business' && (
        <>
          <label style={S.label} htmlFor="acc-team">Default team</label>
          <input id="acc-team" value={f.defaultTeamName} onChange={e => set('defaultTeamName', e.target.value)} placeholder="Main" style={S.input} />
          <p style={S.hint}>Gets the first fax number below, and everyone added to the client joins it. Add more teams later in Users → Teams (each with its own number).</p>
        </>
      )}

      <label style={S.label} htmlFor="acc-name">Name</label>
      <input id="acc-name" value={f.name} onChange={e => set('name', e.target.value)} style={S.input} placeholder="e.g. Riverside Family Practice" />

      <div style={S.label}>Fax numbers <span style={S.labelHint}>(the first is the one faxes are sent from)</span></div>
      {f.numbers.map((n, i) => (
        <div key={i} style={S.row}>
          <input value={n.number} onChange={e => setNum(i, 'number', e.target.value)} placeholder="+1 555 123 4567" aria-label={`Number ${i + 1}`} style={{ ...S.input, flex: '1 1 160px' }}
            />
          <input value={n.label} onChange={e => setNum(i, 'label', e.target.value)} placeholder="Label" aria-label={`Label ${i + 1}`} style={{ ...S.input, flex: '1 1 120px' }} />
          {f.numbers.length > 1 && <button type="button" onClick={() => set('numbers', f.numbers.filter((_, j) => j !== i))} style={S.linkBtn}>Remove</button>}
        </div>
      ))}
      <button type="button" onClick={() => set('numbers', [...f.numbers, { number: '', label: '' }])} style={S.linkBtn}>+ Add a number</button>
      <p style={S.hint}>Each number must be in Telnyx under the <strong>{TELNYX_APP[f.type]}</strong> application.</p>

      {f.type === 'business' && (
        <>
          <label style={S.label} htmlFor="acc-domains">People's email domains <span style={S.labelHint}>(optional, comma separated)</span></label>
          <input id="acc-domains" value={f.domains} onChange={e => set('domains', e.target.value)} placeholder="e.g. example.com" style={S.input} />
          <p style={S.hint}>People with these addresses belong to this client. Leave empty to add people one by one.</p>
        </>
      )}

      {/* 3.18.10: each takes several addresses, separated by commas; Bcc added */}
      <label style={S.label} htmlFor="acc-incoming">Received faxes emailed to <span style={S.labelHint}>(separate several with commas; To, Cc or Bcc: at least one. With Bcc only, nobody sees who else got it)</span></label>
      <input id="acc-incoming" value={f.incoming} onChange={e => set('incoming', e.target.value)} placeholder="team@example.com, lead@example.com" style={S.input} />
      <label style={S.label} htmlFor="acc-incomingcc">Cc <span style={S.labelHint}>(optional, separate several with commas)</span></label>
      <input id="acc-incomingcc" value={f.incomingCc} onChange={e => set('incomingCc', e.target.value)} placeholder="manager@example.com" style={S.input} />
      <label style={S.label} htmlFor="acc-incomingbcc">Bcc <span style={S.labelHint}>(optional, separate several with commas; the others don't see these)</span></label>
      <input id="acc-incomingbcc" value={f.incomingBcc} onChange={e => set('incomingBcc', e.target.value)} placeholder="records@example.com" style={S.input} />
      {/* 3.19.9: a 🛡️ HIPAA badge in the header for this client's people */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '12px 0 4px', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!f.hipaa} onChange={e => set('hipaa', e.target.checked)} />
        {/* 3.54.1 (owner): say everything the tick changes, not just the badge */}
        Handles PHI (HIPAA): <Emo e="🛡️" /> badge, secured fax emails, one number per fax email, confidentiality notice on
        <span style={S.labelHint}>(untick only if this client never faxes health information)</span>
      </label>
      {/* 3.59.2 (owner): faxes to a team's number, when the team has no email of its own */}
      {f.type === 'business' && <>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '12px 0 4px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!f.emailTeamMembers} onChange={e => set('emailTeamMembers', e.target.checked)} />
          Email each incoming fax to everyone in the team that uses the number
        </label>
        {/* 3.59.3: the owner's approved wording */}
        <p style={{ ...S.hint, marginTop: 0 }}>{f.emailTeamMembers
          ? <>When a fax comes to a team’s number:<br />• If the team has its own email (Users → Teams), it goes there.<br />• If not, each person in the team gets their own email.<br />Untick this to send those faxes to the list above instead. Faxes to numbers without a team always go to the list above. Anyone can stop their own emails in Preferences.</>
          : 'Off: faxes to a team’s number go to the team’s own email if it has one, otherwise to the list above.'}</p>
      </>}
      {/* 3.49.21: the Security & compliance report for the client's people */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '12px 0 4px', cursor: 'pointer' }}>
        <input type="checkbox" checked={cr} onChange={e => set('complianceReport', e.target.checked)} />
        Compliance report: its people can download it
      </label>
      <p style={{ ...S.hint, marginTop: 0 }}>{cr
        ? 'On: people whose role allows “Download their client’s Security & compliance report” (Roles & Access) see Compliance and can download it.'
        : 'Off: nobody at the client sees Compliance, whatever their role. Super Admins can still download it.'}
        {' '}{f.type === 'individual' ? 'Off' : 'On'} is the usual choice for {f.type === 'individual' ? 'individual' : 'business'} clients.</p>
      {/* 3.64.0 (owner): Send Confirmation, the same switch as the client's Settings → Fax Settings */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '12px 0 4px', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!f.sendConfirm} onChange={e => set('sendConfirm', e.target.checked)} />
        Send Confirmation: check the receivers and documents before each Compose fax
      </label>
      <p style={{ ...S.hint, marginTop: 0 }}>{f.sendConfirm
        ? 'On: before a fax from Compose goes out, everyone at the client sees every receiver and every document (with a picture of its first page) and confirms. Each confirmation is noted in the Activity log.'
        : 'Off (the usual start): faxes go when Send is clicked.'}
        {' '}The client’s Admins can change it in Settings → Fax Settings → Send Confirmation.</p>
      {/* 3.49.9: cover sheets for faxes sent by email, and the phone they show */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '12px 0 4px', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!f.autoCover} onChange={e => set('autoCover', e.target.checked)} />
        Automatic cover sheet on faxes sent by email
      </label>
      <p style={{ ...S.hint, marginTop: 0 }}>{f.autoCover
        ? 'On: each fax sent by email gets a cover page with the sender’s name, the office number below, the confidentiality notice and Urgent ticked. The cover page counts as a page.'
        : 'Off: faxes sent by email go out without a cover page.'}</p>
      <label style={S.label} htmlFor="acc-officephone">Office contact number <span style={S.labelHint}>(on cover sheets; {f.type === 'individual' ? 'used when the person has no phone in My Profile' : 'Compose starts with it too'})</span></label>
      <input id="acc-officephone" value={f.officePhone} onChange={e => set('officePhone', e.target.value)} placeholder="(800) 766-8732 or +44 20 7946 0958" inputMode="tel" maxLength={24}
        onBlur={() => { const p = checkPhone(f.officePhone); if (p.ok && p.display) set('officePhone', p.display); }}
        aria-invalid={!checkPhone(f.officePhone).ok} style={{ ...S.input, ...(checkPhone(f.officePhone).ok ? {} : { borderColor: 'var(--c-c62828)' }) }} />
      {!checkPhone(f.officePhone).ok && <p style={{ ...S.hint, marginTop: 4, color: 'var(--t-c62828)' }}>That isn't a valid phone number. {PHONE_HELP}</p>}
      {/* 3.57.3: optional; kept for alerts to the client by WhatsApp later */}
      <label style={S.label} htmlFor="acc-whatsapp">WhatsApp number <span style={S.labelHint}>(optional; for alerts by WhatsApp)</span></label>
      <input id="acc-whatsapp" value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="+91 98765 43210 or (800) 766-8732" inputMode="tel" maxLength={24}
        onBlur={() => { const p = checkPhone(f.whatsapp); if (p.ok && p.display) set('whatsapp', p.display); }}
        aria-invalid={!checkPhone(f.whatsapp).ok} style={{ ...S.input, ...(checkPhone(f.whatsapp).ok ? {} : { borderColor: 'var(--c-c62828)' }) }} />
      {!checkPhone(f.whatsapp).ok && <p style={{ ...S.hint, marginTop: 4, color: 'var(--t-c62828)' }}>That isn't a valid WhatsApp number. {PHONE_HELP}</p>}
      {/* 3.61.0 (owner): office hours; the client's Admins can change them in Settings → Office Hours */}
      <label style={S.label}>Office hours <span style={S.labelHint}>(automatic retries and faxes to hard-to-reach numbers only go out in these hours; faxes people send go out at once)</span></label>
      <OfficeHoursFields value={f.officeHours} onChange={v => set('officeHours', v)} disabled={busy} />

      {err && <div role="alert" style={S.error}>{err}</div>}
      <div style={S.actions}>
        <button type="submit" disabled={busy || !!hoursProblem(f.officeHours)} style={S.primaryBtn}>{busy ? 'Saving…' : isNew ? 'Create client' : 'Save'}</button>
        <button type="button" onClick={onCancel} disabled={busy} style={S.ghostBtn}>Cancel</button>
      </div>
    </form>
  );
}

const S = {
  warn: { background: 'var(--c-fff8e1)', border: '1.5px solid var(--c-ffcc80)', borderRadius: 10, padding: '12px 16px', margin: '0 0 14px', fontSize: 13, color: 'var(--t-5d4037)' },
  warnList: { margin: '6px 0', paddingLeft: 20, lineHeight: 1.7 },
  warnLink: { color: 'var(--t-00695c)', fontWeight: 600 },
  warnHint: { color: 'var(--t-795548)', fontSize: 12 },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, maxWidth: 720, lineHeight: 1.5 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 14, padding: 16 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, margin: '10px 0' },
  next: { background: 'var(--c-e8f5e9)', color: 'var(--t-1b5e20)', borderRadius: 10, padding: '12px 16px', fontSize: 14, margin: '0 0 14px' },
  steps: { margin: '6px 0 0', paddingLeft: 20, lineHeight: 1.7 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 0 12px' },
  showGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  showLabel: { fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 },
  select: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle', borderBottom: '1px solid var(--c-f0f0f0)' },
  listRow: { cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '30px 0', color: 'var(--t-78909c)', fontSize: 13 },
  detailCell: { padding: '14px 18px 18px', background: 'var(--c-f7fbfa)', borderBottom: '1px solid var(--c-e0f2f1)' },
  link: { color: 'var(--t-00695c)', fontWeight: 600 },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 },
  name: { fontSize: 16, fontWeight: 700, color: 'var(--t-263238)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  badgeBiz: { fontSize: 11, fontWeight: 700, color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', borderRadius: 20, padding: '2px 9px' },
  badgeInd: { fontSize: 11, fontWeight: 700, color: 'var(--t-6a1b9a)', background: 'var(--c-f3e5f5)', borderRadius: 20, padding: '2px 9px' },
  facts: { margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px 18px' },
  dt: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  dd: { margin: '2px 0 0', fontSize: 13, color: 'var(--t-37474f)', overflowWrap: 'anywhere' },
  form: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 18px', border: '2px solid var(--c-80cbc4)', margin: '0 0 16px', maxWidth: 720 },
  formTitle: { margin: '0 0 6px', fontSize: 16, color: 'var(--t-00695c)' },
  fieldset: { border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  choice: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: 'var(--t-37474f)', cursor: 'pointer' },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '12px 0 4px', padding: 0 },
  labelHint: { textTransform: 'none', letterSpacing: 0, fontWeight: 400 },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit' },
  row: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 },
  hint: { margin: '4px 0 0', fontSize: 12, color: 'var(--t-90a4ae)', lineHeight: 1.45 },
  actions: { display: 'flex', gap: 8, marginTop: 14 },
  primaryBtn: { padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  ghostBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-455a64)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  linkBtn: { padding: '4px 6px', border: 'none', background: 'none', color: 'var(--t-00897b)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
};
